import type { MouseEvent, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import { cx } from '@/lib/cx';
import { fmtInt, isWeekend } from '@/lib/format';

export type Series = { key: string; label: string; color: string; values: number[] };

export const AREA_COLORS = ['var(--s1)', 'var(--s2)', 'var(--s3)'];
export const OTHER_COLOR = 'var(--other)';
const AXIS = { fontSize: 10, fill: 'var(--ink-3)' };

export function niceMax(value: number): number {
  if (!(value > 0)) return 1;
  const power = 10 ** Math.floor(Math.log10(value));
  const n = value / power;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * power;
}

/** Axis max and tick fractions. Count axes use integer multiples of a nice step so every label sits on a gridline. */
function axis(value: number, integer: boolean, fractions: number[]) {
  if (!integer) return { max: niceMax(value), fractions };
  const top = Math.max(1, Math.ceil(value));
  let step = 1;
  for (let power = 1; Math.ceil(top / step) > 3; power *= 10) {
    step = [1, 2, 5, 10].map((m) => m * power).find((c) => Math.ceil(top / c) <= 3) ?? 10 * power;
  }
  const count = Math.ceil(top / step);
  return { max: count * step, fractions: Array.from({ length: count + 1 }, (_, i) => i / count) };
}

function useWidth(fallback: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const w = Math.round(el.getBoundingClientRect().width);
      if (w > 0) setWidth(w);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return { ref, width };
}

function ticks(fractions: number[], max: number, format: (value: number) => string) {
  let prev: string | null = null;
  return fractions.map((f) => {
    const label = format(f * max);
    const show = label !== prev;
    prev = label;
    return { f, label, show };
  });
}

function tickIndexes(count: number, wanted = 5): number[] {
  if (count <= 0) return [];
  if (count === 1) return [0];
  const k = Math.min(wanted, count);
  return Array.from(new Set(Array.from({ length: k }, (_, i) => Math.round((i * (count - 1)) / (k - 1)))));
}

function useHover(count: number, x0: number, band: number, width: number) {
  const ref = useRef<SVGSVGElement>(null);
  const [index, setIndex] = useState<number | null>(null);
  const onMove = (e: MouseEvent<SVGSVGElement>) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect || !count || band <= 0) return;
    const x = ((e.clientX - rect.left) / rect.width) * width;
    const i = Math.floor((x - x0) / band);
    setIndex(i >= 0 && i < count ? i : null);
  };
  return { ref, index, onMove, onLeave: () => setIndex(null) };
}

function Tip({ x, width, children }: { x: number; width: number; children: ReactNode }) {
  const share = x / width;
  const style =
    share > 0.58 ? { right: `calc(${(1 - share) * 100}% + 12px)` } : { left: `calc(${share * 100}% + 12px)` };
  return (
    <div className="tip" style={{ display: 'block', top: 8, ...style }}>
      {children}
    </div>
  );
}

function TipRow({ color, label, value }: { color: string; label: string; value: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <i style={{ width: 9, height: 9, borderRadius: 2, background: color, display: 'inline-block' }} />
      {label}
      <b style={{ marginLeft: 'auto', paddingLeft: 12 }}>{value}</b>
    </div>
  );
}

export function Sparkline({
  values,
  width = 64,
  height = 22,
  bad,
}: {
  values: number[];
  width?: number;
  height?: number;
  bad?: boolean;
}) {
  if (values.length === 0) return <svg className="spark" width={width} height={height} />;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const span = max - min || 1;
  const step = values.length > 1 ? (width - 6) / (values.length - 1) : 0;
  const points = values.map((v, i) => [3 + i * step, height - 3 - ((v - min) / span) * (height - 6)] as const);
  const color = bad ? 'var(--bad)' : 'var(--brand-2)';
  const [lastX, lastY] = points[points.length - 1];
  return (
    <svg className="spark" width={width} height={height} aria-hidden>
      <polyline
        points={points.map((p) => p.join(',')).join(' ')}
        fill="none"
        stroke={color}
        strokeWidth={1.75}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx={lastX} cy={lastY} r={2.5} fill={color} />
    </svg>
  );
}

export function MiniBars({
  values,
  titles,
  width = 92,
  height = 22,
}: {
  values: number[];
  titles?: string[];
  width?: number;
  height?: number;
}) {
  const gap = 2;
  const w = values.length ? (width - gap * (values.length - 1)) / values.length : 0;
  const max = Math.max(1, ...values);
  return (
    <svg width={width} height={height} aria-hidden>
      {values.map((v, i) => {
        const h = v ? Math.max(2, (v / max) * height) : 1;
        return (
          <rect
            key={i}
            x={i * (w + gap)}
            y={height - h}
            width={w}
            height={h}
            rx={1.5}
            fill={v ? 'var(--bad)' : 'var(--line)'}
          >
            {titles?.[i] && <title>{titles[i]}</title>}
          </rect>
        );
      })}
    </svg>
  );
}

function placeEnds(points: { key: string; y: number }[], limit: number, gap = 14): Record<string, number> {
  const sorted = [...points].sort((a, b) => a.y - b.y);
  const ys: number[] = [];
  sorted.forEach((p, i) => {
    ys.push(Math.max(p.y, i ? ys[i - 1] + gap : -Infinity));
  });
  for (let i = ys.length - 1; i >= 0; i--) {
    ys[i] = Math.min(ys[i], i < ys.length - 1 ? ys[i + 1] - gap : limit);
  }
  const out: Record<string, number> = {};
  sorted.forEach((p, i) => {
    out[p.key] = ys[i];
  });
  return out;
}

export function LineChart({
  labels,
  days,
  series,
  width: widthProp = 640,
  height = 230,
  format,
  integer = format === fmtInt,
  title,
  endLabels = true,
  onPick,
}: {
  labels: string[];
  days?: string[];
  series: Series[];
  width?: number;
  height?: number;
  format: (value: number) => string;
  integer?: boolean;
  title: (index: number) => string;
  endLabels?: boolean;
  onPick?: (index: number) => void;
}) {
  const pad = { l: 40, r: endLabels ? 130 : 12, t: 10, b: 22 };
  const { ref: wrapRef, width } = useWidth(widthProp);
  const n = labels.length;
  const band = (width - pad.l - pad.r) / Math.max(1, n);
  const hover = useHover(n, pad.l, band, width);
  const { max, fractions } = axis(Math.max(0, ...series.flatMap((s) => s.values)), integer, [0, 0.25, 0.5, 0.75, 1]);
  const x = (i: number) => pad.l + (i + 0.5) * band;
  const y = (v: number) => pad.t + (1 - v / max) * (height - pad.t - pad.b);
  const ends = placeEnds(
    series.map((s) => ({ key: s.key, y: y(s.values[n - 1] ?? 0) })),
    height - pad.b,
  );
  const at = hover.index;
  return (
    <div className="chartwrap" ref={wrapRef}>
      <svg
        ref={hover.ref}
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        onMouseMove={hover.onMove}
        onMouseLeave={hover.onLeave}
        onClick={() => at !== null && onPick?.(at)}
        style={onPick ? { cursor: 'pointer' } : undefined}
      >
        {days?.map((d, i) =>
          isWeekend(d) ? (
            <rect
              key={d}
              x={pad.l + i * band}
              y={pad.t}
              width={band}
              height={height - pad.t - pad.b}
              fill="var(--sunk)"
              opacity={0.7}
            />
          ) : null,
        )}
        {ticks(fractions, max, format).map(({ f, label, show }) => (
          <g key={f}>
            <line x1={pad.l} x2={width - pad.r} y1={y(f * max)} y2={y(f * max)} stroke="var(--grid)" />
            {show && (
              <text x={pad.l - 6} y={y(f * max) + 3} textAnchor="end" {...AXIS}>
                {label}
              </text>
            )}
          </g>
        ))}
        {tickIndexes(n).map((i) => (
          <text key={i} x={x(i)} y={height - 6} textAnchor="middle" {...AXIS}>
            {labels[i]}
          </text>
        ))}
        {n > 0 &&
          series.map((s) => (
            <polyline
              key={s.key}
              points={s.values.map((v, i) => `${x(i)},${y(v)}`).join(' ')}
              fill="none"
              stroke={s.color}
              strokeWidth={2}
              strokeLinejoin="round"
            />
          ))}
        {n > 0 &&
          endLabels &&
          series.map((s) => {
            const v = s.values[n - 1] ?? 0;
            return (
              <g key={`end-${s.key}`}>
                <circle cx={x(n - 1)} cy={y(v)} r={4} fill={s.color} stroke="var(--panel)" strokeWidth={2} />
                <text x={x(n - 1) + 9} y={ends[s.key] + 4} fontSize={11}>
                  <tspan fontWeight={600} fill="var(--ink)">
                    {s.label}
                  </tspan>
                  <tspan fill="var(--ink-3)" fontWeight={500}>
                    {` ${format(v)}`}
                  </tspan>
                </text>
              </g>
            );
          })}
        {at !== null && <line x1={x(at)} x2={x(at)} y1={pad.t} y2={height - pad.b} stroke="var(--line-strong)" />}
      </svg>
      {at !== null && (
        <Tip x={x(at)} width={width}>
          <b>{title(at)}</b>
          {series.map((s) => (
            <TipRow key={s.key} color={s.color} label={s.label} value={format(s.values[at] ?? 0)} />
          ))}
        </Tip>
      )}
    </div>
  );
}

export function StackedBars({
  labels,
  series,
  width: widthProp = 900,
  height = 200,
  format,
  integer = format === fmtInt,
  title,
}: {
  labels: string[];
  series: Series[];
  width?: number;
  height?: number;
  format: (value: number) => string;
  integer?: boolean;
  title: (index: number) => string;
}) {
  const pad = { l: 48, r: 8, t: 8, b: 22 };
  const { ref: wrapRef, width } = useWidth(widthProp);
  const n = labels.length;
  const band = (width - pad.l - pad.r) / Math.max(1, n);
  const hover = useHover(n, pad.l, band, width);
  const totals = labels.map((_, i) => series.reduce((sum, s) => sum + (s.values[i] ?? 0), 0));
  const { max, fractions } = axis(Math.max(0, ...totals), integer, [0, 0.5, 1]);
  const h = (v: number) => (v / max) * (height - pad.t - pad.b);
  const base = height - pad.b;
  const at = hover.index;
  return (
    <div className="chartwrap" ref={wrapRef}>
      <svg
        ref={hover.ref}
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        onMouseMove={hover.onMove}
        onMouseLeave={hover.onLeave}
      >
        {ticks(fractions, max, format).map(({ f, label, show }) => (
          <g key={f}>
            <line x1={pad.l} x2={width - pad.r} y1={base - h(f * max)} y2={base - h(f * max)} stroke="var(--grid)" />
            {show && (
              <text x={pad.l - 6} y={base - h(f * max) + 3} textAnchor="end" {...AXIS}>
                {label}
              </text>
            )}
          </g>
        ))}
        {labels.map((_, i) => {
          let top = base;
          return (
            <g key={i} opacity={at === null || at === i ? 1 : 0.6}>
              {series.map((s) => {
                const barHeight = h(s.values[i] ?? 0);
                top -= barHeight;
                return (
                  <rect
                    key={s.key}
                    x={pad.l + i * band + 1}
                    y={top}
                    width={Math.max(1, band - 2)}
                    height={barHeight}
                    fill={s.color}
                  />
                );
              })}
            </g>
          );
        })}
        {tickIndexes(n, 6).map((i) => (
          <text key={i} x={pad.l + (i + 0.5) * band} y={height - 6} textAnchor="middle" {...AXIS}>
            {labels[i]}
          </text>
        ))}
      </svg>
      {at !== null && (
        <Tip x={pad.l + (at + 0.5) * band} width={width}>
          <b>
            {title(at)} · {format(totals[at])}
          </b>
          {series.map((s) => (
            <TipRow key={s.key} color={s.color} label={s.label} value={format(s.values[at] ?? 0)} />
          ))}
        </Tip>
      )}
    </div>
  );
}

export function Bars({
  labels,
  values,
  width: widthProp = 520,
  height = 150,
  color = 'var(--bad)',
  format = fmtInt,
  integer = format === fmtInt,
  title,
  unit,
}: {
  labels: string[];
  values: number[];
  width?: number;
  height?: number;
  color?: string;
  format?: (value: number) => string;
  integer?: boolean;
  title: (index: number) => string;
  unit: string;
}) {
  const pad = { l: 30, r: 6, t: 8, b: 22 };
  const { ref: wrapRef, width } = useWidth(widthProp);
  const n = values.length;
  const band = (width - pad.l - pad.r) / Math.max(1, n);
  const hover = useHover(n, pad.l, band, width);
  const { max, fractions } = axis(Math.max(0, ...values), integer, [0, 0.5, 1]);
  const h = (v: number) => (v / max) * (height - pad.t - pad.b);
  const base = height - pad.b;
  const at = hover.index;
  return (
    <div className="chartwrap" ref={wrapRef}>
      <svg
        ref={hover.ref}
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        onMouseMove={hover.onMove}
        onMouseLeave={hover.onLeave}
      >
        {ticks(fractions, max, format).map(({ f, label, show }) => (
          <g key={f}>
            <line x1={pad.l} x2={width - pad.r} y1={base - h(f * max)} y2={base - h(f * max)} stroke="var(--grid)" />
            {show && (
              <text x={pad.l - 6} y={base - h(f * max) + 3} textAnchor="end" {...AXIS}>
                {label}
              </text>
            )}
          </g>
        ))}
        {values.map((v, i) => (
          <rect
            key={i}
            x={pad.l + i * band + 2}
            y={base - h(v)}
            width={Math.max(1, band - 4)}
            height={h(v)}
            rx={2}
            fill={color}
            opacity={at === i ? 1 : 0.85}
          />
        ))}
        {tickIndexes(n, 3).map((i) => (
          <text key={i} x={pad.l + (i + 0.5) * band} y={height - 6} textAnchor="middle" {...AXIS}>
            {labels[i]}
          </text>
        ))}
      </svg>
      {at !== null && (
        <Tip x={pad.l + (at + 0.5) * band} width={width}>
          <b>{title(at)}</b>
          <div>
            {format(values[at])} {unit}
          </div>
        </Tip>
      )}
    </div>
  );
}

export function PanelChart({
  labels,
  bars,
  line,
  width: widthProp = 760,
  height = 250,
  title,
}: {
  labels: string[];
  bars: number[];
  line: (number | null)[];
  width?: number;
  height?: number;
  title: (index: number) => string;
}) {
  const pad = { l: 40, r: 10, t: 16, b: 22 };
  const { ref: wrapRef, width } = useWidth(widthProp);
  const n = labels.length;
  const band = (width - pad.l - pad.r) / Math.max(1, n);
  const hover = useHover(n, pad.l, band, width);
  const split = pad.t + (height - pad.t - pad.b) * 0.55;
  const topH = split - pad.t - 14;
  const bottomTop = split + 14;
  const bottomH = height - pad.b - bottomTop;
  const { max: barMax, fractions: barFractions } = axis(Math.max(0, ...bars), true, [0, 0.5, 1]);
  const rateMax = niceMax(Math.max(0, ...line.map((v) => v ?? 0)));
  const x = (i: number) => pad.l + (i + 0.5) * band;
  const barY = (v: number) => split - 14 - (v / barMax) * topH;
  const fmtRate = (v: number) => `${rateMax < 0.1 ? Math.round(v * 1000) / 10 : Math.round(v * 100)}%`;
  const rateY = (v: number) => bottomTop + (1 - v / rateMax) * bottomH;
  const linePoints = line
    .map((v, i) => (v === null ? null : `${x(i)},${rateY(v)}`))
    .filter((p): p is string => p !== null)
    .join(' ');
  const at = hover.index;
  return (
    <div className="chartwrap" ref={wrapRef}>
      <svg
        ref={hover.ref}
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        onMouseMove={hover.onMove}
        onMouseLeave={hover.onLeave}
      >
        <text x={pad.l} y={pad.t - 4} fontSize={10} fontWeight={700} fill="var(--ink-3)">
          ASKS PER DAY
        </text>
        {ticks(barFractions, barMax, fmtInt).map(({ f, label, show }) => (
          <g key={`b${f}`}>
            <line x1={pad.l} x2={width - pad.r} y1={barY(f * barMax)} y2={barY(f * barMax)} stroke="var(--grid)" />
            {show && (
              <text x={pad.l - 6} y={barY(f * barMax) + 3} textAnchor="end" {...AXIS}>
                {label}
              </text>
            )}
          </g>
        ))}
        {bars.map((v, i) => (
          <rect
            key={i}
            x={pad.l + i * band + 1}
            y={barY(v)}
            width={Math.max(1, band - 2)}
            height={split - 14 - barY(v)}
            fill="var(--brand-2)"
            opacity={0.85}
          />
        ))}
        <text x={pad.l} y={bottomTop - 4} fontSize={10} fontWeight={700} fill="var(--ink-3)">
          NOT-OK RATE
        </text>
        {ticks([0, 0.5, 1], rateMax, fmtRate).map(({ f, label, show }) => (
          <g key={`r${f}`}>
            <line x1={pad.l} x2={width - pad.r} y1={rateY(f * rateMax)} y2={rateY(f * rateMax)} stroke="var(--grid)" />
            {show && (
              <text x={pad.l - 6} y={rateY(f * rateMax) + 3} textAnchor="end" {...AXIS}>
                {label}
              </text>
            )}
          </g>
        ))}
        {linePoints && (
          <polyline points={linePoints} fill="none" stroke="var(--bad)" strokeWidth={2} strokeLinejoin="round" />
        )}
        {tickIndexes(n).map((i) => (
          <text key={i} x={x(i)} y={height - 6} textAnchor="middle" {...AXIS}>
            {labels[i]}
          </text>
        ))}
        {at !== null && <line x1={x(at)} x2={x(at)} y1={pad.t} y2={height - pad.b} stroke="var(--line-strong)" />}
      </svg>
      {at !== null && (
        <Tip x={x(at)} width={width}>
          <b>{title(at)}</b>
          <div>{fmtInt(bars[at])} asks</div>
          <div style={{ color: 'var(--bad)' }}>
            ● {line[at] === null ? '–' : `${Math.round((line[at] ?? 0) * 1000) / 10}%`} not ok
          </div>
        </Tip>
      )}
    </div>
  );
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function Heatmap({ grid, onPick }: { grid: number[][]; onPick?: (day: number, hour: number) => void }) {
  const max = Math.max(1, ...grid.flat());
  const alpha = (n: number) => (n === 0 ? 0.04 : 0.12 + 0.78 * (n / max));
  return (
    <div className="heat">
      <span />
      {Array.from({ length: 24 }, (_, h) => (
        <span key={h} style={{ textAlign: 'center' }}>
          {h % 3 === 0 ? h : ''}
        </span>
      ))}
      {grid.map((row, d) => (
        <HeatRow
          key={d}
          label={WEEKDAYS[d]}
          row={row}
          alpha={alpha}
          onPick={onPick ? (h) => onPick(d, h) : undefined}
        />
      ))}
    </div>
  );
}

function HeatRow({
  label,
  row,
  alpha,
  onPick,
}: {
  label: string;
  row: number[];
  alpha: (n: number) => number;
  onPick?: (hour: number) => void;
}) {
  return (
    <>
      <span>{label}</span>
      {row.map((n, h) => (
        <i
          key={h}
          className={cx(onPick && 'clickable')}
          title={`${label} ${h}:00 UTC · ${fmtInt(n)} asks`}
          style={{ background: `rgb(var(--heat) / ${alpha(n)})` }}
          onClick={onPick ? () => onPick(h) : undefined}
        />
      ))}
    </>
  );
}
